import React, { useEffect, useState } from 'react';
import { Check, Download, Printer } from 'lucide-react';

import { downloadShippingLabel, loadShippingPrinters, printShippingLabel } from './api';
import { tx } from './i18n';
import { errorMessage } from './ui';
import './devices.css';

// A carrier label this API holds, the only kind a printer can be sent.
const CARRIER_LABEL = /^\/Carrier\/shipments\/[^/]+\/labels\/[^/]+$/;
const PRINTER_KEY = 'xomics-shipping-printer';
const PRINTERS_FRESH_MS = 30000;
let printersRead = null;
let printersReadAt = 0;

// The connected shipping-label printers of this location, read once for every
// label button on the screen. An account that may not see devices, or a
// location without such a printer, simply gets no Print button.
function shippingPrinters() {
  if (printersRead === null || Date.now() - printersReadAt > PRINTERS_FRESH_MS) {
    printersReadAt = Date.now();
    printersRead = loadShippingPrinters().then(devices => devices.filter(device => device.paired), () => []);
  }
  return printersRead;
}

export function isPrintableLabel(labelUrl) {
  try { return CARRIER_LABEL.test(new URL(labelUrl, window.location.origin).pathname); } catch (error) { return false; }
}

function remembered() { try { return window.localStorage.getItem(PRINTER_KEY) || ''; } catch (error) { return ''; } }
function remember(value) { try { window.localStorage.setItem(PRINTER_KEY, value); } catch (error) { /* kept for this visit only */ } }

export const printerName = device => `${device.type.model} · ${device.deviceId}`;

// The location's connected shipping printers and the one in use on this
// computer: the one chosen last, or the first.
export function useShippingPrinters() {
  const [printers, setPrinters] = useState([]);
  const [chosen, setChosen] = useState(remembered());
  useEffect(() => {
    let live = true;
    shippingPrinters().then((devices) => { if (live) setPrinters(devices); });
    return () => { live = false; };
  }, []);
  return {
    printers,
    printer: printers.find(device => device.deviceId === chosen) || printers[0] || null,
    choose(deviceId) { setChosen(deviceId); remember(deviceId); },
  };
}

// The printer picker, shown as soon as there is more than one to pick from.
export function ShippingPrinterSelect({ shipping, disabled = false }) {
  if (shipping.printers.length < 2) return null;
  return (
    <select className="xo-label-printer" value={shipping.printer.deviceId} aria-label={tx('ui.devices.print.choose')} disabled={disabled}
      onChange={event => shipping.choose(event.target.value)}>
      {shipping.printers.map(device => <option key={device.deviceId} value={device.deviceId}>{printerName(device)}</option>)}
    </select>
  );
}

// Sends the label straight to a shipping printer of this location, and says
// which one.
function PrintLabelButton({ labelUrl }) {
  const shipping = useShippingPrinters();
  const [state, setState] = useState('idle'), [error, setError] = useState('');
  if (shipping.printer === null) return null;
  async function print() {
    if (state === 'busy') return;
    setState('busy'); setError('');
    try { await printShippingLabel(labelUrl, shipping.printer.deviceId); setState('done'); }
    catch (requestError) { setError(errorMessage(requestError)); setState('idle'); }
  }
  // With several printers the picker beside the button names the one in use,
  // so the button says only Print.
  const picker = shipping.printers.length > 1;
  return <>
    <ShippingPrinterSelect shipping={{ ...shipping, choose(deviceId) { shipping.choose(deviceId); setState('idle'); } }} disabled={state === 'busy'} />
    <button type="button" className="xo-link xo-label-print" disabled={state === 'busy'} onClick={print}>
      {state === 'done' ? <Check aria-hidden="true" /> : <Printer aria-hidden="true" />}
      {' '}{state === 'busy' ? tx('ui.devices.print.printing') : state === 'done' ? tx('ui.devices.print.done')
        : picker ? tx('ui.collections.print') : tx('ui.devices.print.on', { printer: printerName(shipping.printer) })}
    </button>
    {error && <small role="alert">{error}</small>}
  </>;
}

function triggerDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export default function ShippingLabelButton({ labelUrl, children, className = '', printable = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function download() {
    setBusy(true);
    setError('');
    try {
      const result = await downloadShippingLabel(labelUrl);
      if (result.externalUrl !== null) {
        window.open(result.externalUrl, '_blank', 'noopener,noreferrer');
      } else {
        triggerDownload(result.blob, result.filename);
      }
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className={className}>
      <button type="button" className="xo-link" disabled={busy} onClick={download}>
        <Download aria-hidden="true" /> {busy ? tx('ui.carrier.preparing_label') : children}
      </button>
      {error && <small role="alert">{error}</small>}
      {printable && isPrintableLabel(labelUrl) && <PrintLabelButton labelUrl={labelUrl} />}
    </span>
  );
}

// Every label of a shipment as its own download, each with its customs invoice
// when it has one.
export function ShipmentLabelButtons({ labels, className }) {
  return labels.map((label) => {
    const name = label.direction === 'outbound'
      ? tx('ui.orders.outbound_label_5a21bb14')
      : tx('ui.carrier.return_label_n', { n: label.sequence });
    return (
      <React.Fragment key={`${label.direction}-${label.sequence}`}>
        <ShippingLabelButton labelUrl={label.labelUrl} className={className} printable>{name}</ShippingLabelButton>
        {label.invoiceUrl && (
          <ShippingLabelButton labelUrl={label.invoiceUrl} className={className}>
            {name} · {tx('ui.carrier.customs_invoice')}
          </ShippingLabelButton>
        )}
      </React.Fragment>
    );
  });
}
